import { TickerPayload } from '@helpers/utils/price-ws/price-ws';
import { useLivePriceWebSocketStore } from '@terminal/stores/use-live-price-websocket-store/useLivePriceWebSocketStore';
import { create } from '@terminal/utils/store/createStore';
import { StoreApi } from 'zustand';

import {
  getLivePriceDataKey,
  getLivePriceTicker,
  Identifier,
  LivePriceData,
  LivePriceState,
  mapNewLivePriceDataToWatchedData,
  mapPriceEntityToLivePriceData,
  PriceEntity,
  priceUpdater,
  SubscribeConfig,
  updateWatching,
} from './useLivePriceStore.utils';

const unsubscribe =
  ({
    tickers,
    priceEntities,
    get,
    set,
    identifier,
  }: {
    tickers: TickerPayload[];
    priceEntities: PriceEntity[];
    get: StoreApi<LivePriceState>['getState'];
    set: StoreApi<LivePriceState>['setState'];
    identifier: Identifier;
  }) =>
  () => {
    const { unsubscribe: unsubscribeWS } =
      useLivePriceWebSocketStore.getState();

    if (!tickers.length) {
      return;
    }

    priceEntities.forEach(({ entity }) => {
      const isDeleted = updateWatching(
        set,
        get,
        identifier,
        getLivePriceDataKey(entity)
      );
      // Built from this entity, not indexed out of `tickers`: that array is a
      // subset of `priceEntities` (entities with no price, or already in the
      // store, are skipped), so pairing by position hands the WS another
      // entity's payload, or `undefined` once the subset is shorter.
      const ticker = getLivePriceTicker(entity);

      if (isDeleted && ticker) {
        unsubscribeWS([ticker]);
      }
    });

    const withoutTickers = Object.keys(
      get().componentsWatchingData
    ).reduce<LivePriceData>((livePriceData, key) => {
      return { ...livePriceData, [key]: get().livePriceData[key] };
    }, {});

    set({ livePriceData: withoutTickers });
  };

export const useLivePriceStore = create<LivePriceState>((set, get) => ({
  livePriceData: {},
  componentsWatchingData: {},
  subscribe: ({
    priceEntities,
    identifier = Identifier.Global,
    delayedPrices,
  }: SubscribeConfig) => {
    const { livePriceData, componentsWatchingData } = get();

    const { data: newLivePriceData, tickers } = mapPriceEntityToLivePriceData(
      priceEntities,
      livePriceData
    );

    const watchingLivePriceData = mapNewLivePriceDataToWatchedData(
      priceEntities,
      newLivePriceData,
      identifier,
      componentsWatchingData
    );

    const { subscribe: subscribeWS } = useLivePriceWebSocketStore.getState();

    subscribeWS({ tickers, updateHandler: priceUpdater(set), delayedPrices });

    set({
      livePriceData: { ...livePriceData, ...newLivePriceData },
      componentsWatchingData: watchingLivePriceData,
    });

    return unsubscribe({ tickers, priceEntities, get, set, identifier });
  },
}));
